import {
  loadRuntimeApiConfig,
  logStagingZetaApiUsage
} from '../common/config/runtime-api-config';

class ArticleRelated {
  constructor(root, runtimeConfig = {}) {
    this.root = root;
    this.tagsRoot = this.resolveTarget(
      root.dataset.tagsSelector,
      '.js-article-related-tags',
      '#article-tags'
    );
    this.articlesRoot = this.resolveTarget(
      root.dataset.articlesSelector,
      '.js-article-related-articles',
      '#other-articles'
    );

    if (!this.tagsRoot || !this.articlesRoot) return;

    this.tagTemplate = this.resolveTemplate(this.tagsRoot);
    this.articleTemplate = this.resolveTemplate(this.articlesRoot);
    this.moreButton = this.resolveMoreButton();
    this.moreButtonContainer = this.resolveMoreButtonContainer();
    this.articleSection = this.articlesRoot.closest('.l-article-section');

    this.tagEndpoint =
      this.tagsRoot.dataset.endpoint ||
      runtimeConfig.hashtagEndpoint ||
      'https://dev.shiseido.search.zetacx.net/api/hashtag/kaihobiku-item';
    this.articleEndpoint =
      this.articlesRoot.dataset.endpoint ||
      runtimeConfig.searchEndpoint ||
      'https://dev.shiseido.search.zetacx.net/api/search/kaihobiku';
    this.tagSearchPath = '/kaiho-biku/search/';
    this.fields =
      this.articlesRoot.dataset.fields || 'id,title,url,thumbnail,date,tags';
    this.currentId = (this.tagsRoot.dataset.id || '').trim();
    this.fixedArticleTags = this.parseFixedArticleTags();
    this.tagLimit = this.toInt(
      this.tagsRoot.dataset.count || this.tagsRoot.dataset.initialCount,
      3
    );
    this.initialCount = this.toInt(this.articlesRoot.dataset.initialCount, 4);
    this.moreCount = this.toInt(this.articlesRoot.dataset.moreCount, 6);

    this.tags = [];
    this.renderedCount = 0;
    this.total = Infinity;
    this.exhausted = false;
    this.loading = false;

    this.init();
  }

  resolveTarget(customSelector, defaultSelector, fallbackSelector) {
    if (customSelector) {
      const target = this.root.querySelector(customSelector);
      if (target) return target;
    }

    const defaultTarget = this.root.querySelector(defaultSelector);
    if (defaultTarget) return defaultTarget;

    return document.querySelector(fallbackSelector);
  }

  toInt(value, fallback) {
    const num = Number(value);
    return Number.isFinite(num) && num >= 0 ? num : fallback;
  }

  resolveTemplate(root) {
    const templateId = root.dataset.templateId;
    if (templateId) {
      const template = document.getElementById(templateId);
      if (template) return template;
    }
    return root.querySelector('template');
  }

  resolveMoreButton() {
    if (this.articlesRoot.dataset.moreButtonSelector) {
      const localButton = this.root.querySelector(
        this.articlesRoot.dataset.moreButtonSelector
      );
      if (localButton) return localButton;
    }

    if (this.articlesRoot.dataset.moreButtonSelector) {
      return document.querySelector(this.articlesRoot.dataset.moreButtonSelector);
    }

    if (this.articlesRoot.id) {
      const localButton = this.root.querySelector(
        `[data-more-for="${this.articlesRoot.id}"]`
      );
      if (localButton) return localButton;
    }

    if (this.articlesRoot.id) {
      return document.querySelector(`[data-more-for="${this.articlesRoot.id}"]`);
    }
    return null;
  }

  resolveMoreButtonContainer() {
    if (!this.moreButton) return null;
    return this.moreButton.closest('.m-card-list-more');
  }

  normalizeTagItems(json) {
    const result = json?.result ?? json ?? {};
    const rawItems = Array.isArray(result.items)
      ? result.items
      : Array.isArray(result.hashtags)
      ? result.hashtags
      : Array.isArray(result.list)
      ? result.list
      : Array.isArray(json)
      ? json
      : [];

    return rawItems
      .map((item) => {
        if (typeof item === 'string') return item;
        return item.tag ?? item.hashtag ?? item.name ?? item.label ?? '';
      })
      .map((tag) => String(tag).replace(/^#/, '').trim())
      .filter(Boolean);
  }

  parseFixedArticleTags() {
    const raw = this.articlesRoot.dataset.hashtags || '';

    return Array.from(
      new Set(
        String(raw)
          .split(',')
          .map((tag) => String(tag).replace(/^#/, '').trim())
          .filter(Boolean)
      )
    );
  }

  normalizeArticleResponse(json) {
    const result = json?.result ?? json ?? {};
    const itemsRaw = Array.isArray(result.items)
      ? result.items
      : Array.isArray(result.list)
      ? result.list
      : [];

    const items = itemsRaw.map((item) => {
      const mapped = { ...item };
      if (mapped.tags != null && mapped.hashtag == null) {
        mapped.hashtag = mapped.tags;
      }
      if (mapped.imagepath != null && mapped.thumbnail == null) {
        mapped.thumbnail = mapped.imagepath;
      }
      mapped.dateFormatted = this.formatDate(mapped.date);
      return mapped;
    });

    const totalRaw = result?.attribute?.total ?? result?.total ?? json?.total ?? items.length;
    const total = Number(totalRaw);

    return {
      items,
      total: Number.isFinite(total) ? total : items.length
    };
  }

  parseDate(raw) {
    if (!raw) return null;
    const text = String(raw).trim();

    if (/^\d{4}-\d{2}-\d{2}/.test(text)) return new Date(text);
    if (/^\d{4}\.\d{2}\.\d{2}$/.test(text)) {
      const [year, month, day] = text.split('.');
      return new Date(`${year}-${month}-${day}`);
    }
    if (/^\d{8}$/.test(text)) {
      const year = text.slice(0, 4);
      const month = text.slice(4, 6);
      const day = text.slice(6, 8);
      return new Date(`${year}-${month}-${day}`);
    }

    const date = new Date(text);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  formatDate(raw) {
    const date = this.parseDate(raw);
    if (!date) return raw || '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  }

  createTagUrl(tag) {
    const separator = this.tagSearchPath.includes('?') ? '&' : '?';
    return `${this.tagSearchPath}${separator}hashtag=${encodeURIComponent(tag)}`;
  }

  buildTagRequest() {
    const params = new URLSearchParams();
    params.set('id', this.currentId);
    params.set('limit', String(this.tagLimit));
    return `${this.tagEndpoint}?${params.toString()}`;
  }

  buildArticleRequest(limit, offset) {
    const params = new URLSearchParams();
    params.set('hashtag', this.tags.join(','));
    params.set('a', this.fields);
    params.set('l', String(limit));
    params.set('o', String(offset));

    if (this.currentId) {
      params.set('id__not', this.currentId);
    }

    return `${this.articleEndpoint}?${params.toString()}`;
  }

  async fetchTags() {
    if (!this.tagEndpoint) {
      throw new Error('Hashtag API endpoint is not configured');
    }

    logStagingZetaApiUsage(this.tagEndpoint);
    const response = await fetch(this.buildTagRequest(), { credentials: 'omit' });
    if (!response.ok) {
      throw new Error(`Hashtag API failed: ${response.status}`);
    }
    const json = await response.json();
    return Array.from(new Set(this.normalizeTagItems(json)));
  }

  async fetchArticles(limit, offset) {
    if (!this.articleEndpoint) {
      throw new Error('Article API endpoint is not configured');
    }

    logStagingZetaApiUsage(this.articleEndpoint);
    const response = await fetch(this.buildArticleRequest(limit, offset), {
      credentials: 'omit'
    });
    if (!response.ok) {
      throw new Error(`Article API failed: ${response.status}`);
    }
    const json = await response.json();
    return this.normalizeArticleResponse(json);
  }

  clearRenderedItems(root) {
    Array.from(root.children).forEach((child) => {
      if (child.tagName !== 'TEMPLATE') {
        child.remove();
      }
    });
  }

  bindItem(fragment, item) {
    fragment.querySelectorAll('[data-bind-text]').forEach((element) => {
      const key = element.getAttribute('data-bind-text');
      element.textContent = item[key] != null ? String(item[key]) : '';
    });

    fragment.querySelectorAll('[data-bind-href]').forEach((element) => {
      const key = element.getAttribute('data-bind-href');
      element.setAttribute('href', item[key] != null ? String(item[key]) : '#');
    });

    fragment.querySelectorAll('[data-bind-src]').forEach((element) => {
      const key = element.getAttribute('data-bind-src');
      element.setAttribute('src', item[key] != null ? String(item[key]) : '');
    });

    if (item.id) {
      const categoryCode = String(item.id).split('_')[0];
      fragment.querySelectorAll('.m-card').forEach((element) => {
        element.classList.add(`--category${categoryCode}`);
      });
    }
  }

  appendRows(root, template, items) {
    if (!template) return;

    const insertBeforeNode = template.parentElement === root ? template : null;
    const fragment = document.createDocumentFragment();

    items.forEach((item) => {
      const node = template.content
        ? template.content.cloneNode(true)
        : document.importNode(template, true);
      this.bindItem(node, item);
      fragment.appendChild(node);
    });

    if (insertBeforeNode) {
      root.insertBefore(fragment, insertBeforeNode);
      return;
    }

    root.appendChild(fragment);
  }

  renderTags(tags) {
    const rows = tags.map((tag) => ({
      tagLabel: `#${tag}`,
      tagUrl: this.createTagUrl(tag)
    }));

    this.clearRenderedItems(this.tagsRoot);
    this.appendRows(this.tagsRoot, this.tagTemplate, rows);
  }

  appendArticles(items) {
    this.appendRows(this.articlesRoot, this.articleTemplate, items);
    this.renderedCount += items.length;
  }

  updateMoreButton() {
    const noMore =
      this.moreCount <= 0 ||
      this.exhausted ||
      this.renderedCount >= this.total;

    if (this.moreButton) {
      this.moreButton.hidden = noMore;
    }
    if (this.moreButtonContainer) {
      this.moreButtonContainer.hidden = noMore;
    }
  }

  updateArticleSectionVisibility() {
    if (!this.articleSection) return;
    this.articleSection.hidden = this.renderedCount === 0;
  }

  async renderArticleBatch(limit, offset) {
    if (this.loading || !this.tags.length) return;

    this.loading = true;
    try {
      const { items, total } = await this.fetchArticles(limit, offset);
      this.total = total;
      if (items.length) {
        this.appendArticles(items);
      }
      if (items.length < limit) {
        this.exhausted = true;
      }
      this.updateMoreButton();
      this.updateArticleSectionVisibility();
    } catch (error) {
      console.error('[article-related] render error:', error);
      this.updateMoreButton();
      this.updateArticleSectionVisibility();
    } finally {
      this.loading = false;
    }
  }

  async init() {
    if (!this.currentId || !this.tagTemplate || !this.articleTemplate) return;
    if (!this.tagEndpoint || !this.articleEndpoint) {
      console.error('[article-related] API endpoint is not configured');
      this.updateMoreButton();
      this.updateArticleSectionVisibility();
      return;
    }

    try {
      const displayTags = await this.fetchTags();
      this.renderTags(displayTags);
      this.tags = this.fixedArticleTags.length
        ? this.fixedArticleTags
        : displayTags;

      if (!this.tags.length) {
        this.updateMoreButton();
        this.updateArticleSectionVisibility();
        return;
      }

      await this.renderArticleBatch(this.initialCount, 0);

      if (this.moreButton && this.moreCount > 0) {
        this.moreButton.addEventListener('click', () => {
          this.renderArticleBatch(this.moreCount, this.renderedCount);
        });
      }
    } catch (error) {
      console.error('[article-related] init error:', error);
      this.updateMoreButton();
      this.updateArticleSectionVisibility();
    }
  }
}

export default ArticleRelated;

async function boot() {
  const runtimeConfig = await loadRuntimeApiConfig();
  const root = document.querySelector('.js-article-related');
  if (!root) return;
  new ArticleRelated(root, runtimeConfig);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
